import type { FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import type { ReactiveFormBuilderFileUploadAdapter } from '../../adapters/reactive-form-builder.adapters';
import type { Form, FormSignatureConfiguration } from '../../interfaces/form.interface';

export interface ReactiveFormMediaFacadeConfiguration<T> {
  readonly getForm: () => Form<T> | null;
  readonly getModel: () => FormGroup | null;
  readonly fileUploadService: ReactiveFormBuilderFileUploadAdapter;
  readonly markForCheck: () => void;
}

const DEFAULT_SIGNATURE_CONFIGURATION: FormSignatureConfiguration = {
  database: 'tareas_fitosanitarias',
  folder: 'fitosanitarios',
  field: 'signature_img',
  uploadStrategy: 'taskFile',
};

/**
 * Owns media mutations for signatures and uploaded image fields.
 *
 * The builder component keeps the template-facing methods, while this facade centralizes upload strategy selection,
 * signature field resolution and model patching side effects.
 */
export class ReactiveFormMediaFacade<T> {
  constructor(private readonly configuration: ReactiveFormMediaFacadeConfiguration<T>) {}

  public async getSignature(file: File): Promise<void> {
    const model = this.configuration.getModel();

    if (!model) {
      return;
    }

    const signatureConfiguration = this.getSignatureConfiguration();
    const signatureField = this.getSignatureField(signatureConfiguration);

    if (signatureConfiguration.uploadStrategy === 'deferredBase64') {
      this.configuration.markForCheck();
      return;
    }

    model.patchValue({ [signatureField]: file });

    const signatureFile = model.get(signatureField)?.value as File | null;

    if (signatureFile == null) {
      return;
    }

    const uploadedFileName = await firstValueFrom(
      this.configuration.fileUploadService.uploadTaskFile(
        signatureFile,
        signatureConfiguration.folder,
        signatureConfiguration.database ?? DEFAULT_SIGNATURE_CONFIGURATION.database ?? '',
        signatureField,
      ),
    );

    if (!uploadedFileName) {
      return;
    }

    model.patchValue({ [signatureField]: uploadedFileName });
    this.configuration.markForCheck();
  }

  public updateFirma(signatureImage: string): void {
    const model = this.configuration.getModel();

    if (!model) {
      return;
    }

    const signatureField = this.getSignatureField(this.getSignatureConfiguration());

    model.patchValue({ [signatureField]: signatureImage });
    this.configuration.markForCheck();
  }

  public updateImageModel(imageName: string, fieldName: string): void {
    const model = this.configuration.getModel();

    if (!model || fieldName.trim().length === 0) {
      return;
    }

    model.patchValue({ [fieldName]: imageName });
  }

  private getSignatureConfiguration(): FormSignatureConfiguration {
    return this.configuration.getForm()?.signatureConfiguration ?? DEFAULT_SIGNATURE_CONFIGURATION;
  }

  private getSignatureField(signatureConfiguration: FormSignatureConfiguration): string {
    return signatureConfiguration.field ?? DEFAULT_SIGNATURE_CONFIGURATION.field ?? 'signature_img';
  }
}
